import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TChannelProtocol } from '@/entities/channel';
import { partnerAccountQuery } from '@/entities/partner';
import { useProfile } from '@/entities/profile';
import { isPartnerImpersonation } from '@/features/impersonation';
import { ApiError } from '@/shared/api/ApiError';
import { globalOnError } from '@/shared/lib/globalOnError';
import { useAppQuery } from '@/shared/lib/useAppQuery';
import { ITariffGridDto, ITariffPlanDto } from '@/shared/api/subscriptions';
import { useNotifications } from '@/shared/ui/Notifications';

import { useCreateSubscription } from './useCreateSubscription';
import { useOrderConfirmation } from './useOrderConfirmation';
import { useRenewSubscription } from './useRenewSubscription';
import {
  PARTNER_ACCOUNT_PAYMENT_METHOD_ID,
  PAYMENTS_METHODS,
  PAYMENTS_METHODS_MOCK,
} from './constants';
import {
  getPaymentRedirectLink,
  navigateToChannels,
  openReservedPaymentTab,
  reservePaymentTab,
} from './paymentRedirect';

type DialogStep = 'selectTariff' | 'payment';

interface IUseBillingDialogProps {
  channelProtocol?: TChannelProtocol;
  isRenewing?: boolean;
  channelId?: string;
}

export const useBillingDialog = ({
  channelProtocol,
  isRenewing,
  channelId,
}: IUseBillingDialogProps) => {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<DialogStep>('selectTariff');
  const [selectedTariff, setSelectedTariff] = useState<ITariffGridDto>();
  const [selectedPlan, setSelectedPlan] = useState<ITariffPlanDto>();
  const isPartnerPayment = isPartnerImpersonation();
  const { data: partnerAccount } = useAppQuery({
    ...partnerAccountQuery(),
    enabled: isPartnerPayment,
  });
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
    isPartnerPayment ? PARTNER_ACCOUNT_PAYMENT_METHOD_ID : 2
  );
  const reservedPaymentTabRef = useRef<Window | null>(null);

  const { profile } = useProfile();
  const email = profile?.email;
  const accountId = profile?.account_id;
  const { confirmOrder, isPending: isConfirmingOrder } = useOrderConfirmation();
  const { createSubscription, isPending: isCreatingSubscription } =
    useCreateSubscription();
  const { renewSubscription, isPending: isRenewingSubscription } =
    useRenewSubscription();
  const { addNotification } = useNotifications();

  const isPending =
    isConfirmingOrder || isCreatingSubscription || isRenewingSubscription;

  const resetState = () => {
    setCurrentStep('selectTariff');
    setSelectedTariff(undefined);
    setSelectedPlan(undefined);
    setSelectedPaymentMethodId(
      isPartnerPayment ? PARTNER_ACCOUNT_PAYMENT_METHOD_ID : 2
    );
  };

  const getSelectedPaymentMethod = () => {
    if (isPartnerPayment) {
      return 'card';
    }

    const selectedPaymentMethod = PAYMENTS_METHODS_MOCK.find(
      (method) => method.id === selectedPaymentMethodId
    );

    return selectedPaymentMethod?.type === PAYMENTS_METHODS.PAYMENTS_ACCOUNT
      ? 'bank_invoice'
      : 'card';
  };

  const reservePaymentTabForCurrentMethod = () => {
    if (
      currentStep !== 'payment' ||
      getSelectedPaymentMethod() !== 'bank_invoice'
    ) {
      return;
    }

    if (reservedPaymentTabRef.current && !reservedPaymentTabRef.current.closed) {
      return;
    }

    reservedPaymentTabRef.current = reservePaymentTab();
  };

  const handlePayment = async (onPaymentComplete?: () => void) => {
    if (
      selectedTariff &&
      selectedPlan &&
      channelProtocol &&
      accountId &&
      email
    ) {
      const body = {
        subscriptionData: {
          tariff_id: selectedTariff.tariff_info.tariff_id,
          interval: selectedPlan.interval,
          channel_type: channelProtocol,
        },
        params: { account_id: accountId },
      };

      const paymentMethod = getSelectedPaymentMethod();
      const reservedPaymentTab =
        paymentMethod === 'bank_invoice'
          ? reservedPaymentTabRef.current ?? reservePaymentTab()
          : null;
      reservedPaymentTabRef.current = reservedPaymentTab;

      try {
        let orderToConfirm;
        if (!isRenewing) {
          orderToConfirm = await createSubscription(body);
        } else {
          orderToConfirm = await renewSubscription({
            subscriptionData: body.subscriptionData,
            params: { channel_id: channelId ?? '', ...body.params },
          });
        }

        const response = await confirmOrder({
          order_id: orderToConfirm.order_id,
          email,
          payment_method: paymentMethod,
        });

        const paymentLink = getPaymentRedirectLink(response);

        if (!paymentLink) {
          reservedPaymentTab?.close();
          reservedPaymentTabRef.current = null;
          addNotification({
            type: 'error',
            title: t('common:errors.generic'),
          });
          return;
        }

        if (paymentMethod === 'bank_invoice') {
          openReservedPaymentTab(
            paymentLink,
            reservedPaymentTab
          );
          resetState();
          onPaymentComplete?.();
          navigateToChannels();
          reservedPaymentTabRef.current = null;
          return;
        }

        window.location.assign(paymentLink);
      } catch (error) {
        reservedPaymentTab?.close();
        reservedPaymentTabRef.current = null;
        globalOnError(ApiError.fromError(error), t, addNotification);
      }
    } else {
      addNotification({
        type: 'error',
        title: t('billing:errors.selectTariffAndPlan'),
      });
    }
  };

  const handleBack = (onBack: () => void) => {
    if (currentStep === 'payment') {
      setCurrentStep('selectTariff');
    } else {
      onBack();
    }
  };

  const handleContinue = (onPaymentComplete?: () => void) => {
    if (currentStep === 'selectTariff') {
      if (selectedTariff && selectedPlan) {
        setCurrentStep('payment');
      } else {
        addNotification({
          type: 'error',
          title: t('billing:errors.selectTariffAndPlan'),
        });
      }
    } else if (currentStep === 'payment') {
      handlePayment(onPaymentComplete);
    }
  };

  const getContinueButtonText = () => {
    if (currentStep === 'selectTariff') {
      return t('common:buttons.continue');
    }
    return t('billing:payment.actions.pay');
  };

  const handleClose = (onClose: () => void) => {
    resetState();
    onClose();
  };

  return {
    currentStep,
    selectedTariff,
    selectedPlan,
    selectedPaymentMethodId,
    isPartnerPayment,
    partnerDiscountRate: partnerAccount?.rates.partnerDiscountRate,
    isPending,

    setSelectedTariff,
    setSelectedPlan,
    setSelectedPaymentMethodId,

    handleBack,
    handleContinue,
    handleClose,
    reservePaymentTabForCurrentMethod,
    getContinueButtonText,
  };
};
